import React, { useState } from 'react';
import Select from 'react-select';
import { X, Plus, Trash2, MessageSquare } from 'lucide-react';

interface IntentRule {
    phrase: string;
    requiredEntities: string[];
    priority: number;
}

const mockWorkflowOptions = [
    { value: 'Generate Invoice', label: 'Generate Invoice' },
    { value: 'Send Email', label: 'Send Email' },
    { value: 'Create Contact', label: 'Create Contact' },
    { value: 'Apply Leave', label: 'Apply Leave' }
];

const CreateIntentModelModal = ({ onClose }: { onClose: () => void }) => {
    const [intentName, setIntentName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [userOptions, setUserOptions] = useState<any[]>([]);   
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const [linkedWorkflows, setLinkedWorkflows] = useState<string[]>([]);
    const [rules, setRules] = useState<IntentRule[]>([
        { phrase: '', requiredEntities: [''], priority: 1 }
    ]);
    const [sampleInput, setSampleInput] = useState('');
    const [testOutput, setTestOutput] = useState<string>('');

    const addRule = () => {
        setRules([...rules, { phrase: '', requiredEntities: [''], priority: 1 }]);
    };

    const updateRule = <K extends keyof IntentRule>(
        index: number,
        key: K,
        value: IntentRule[K]
    ) => {
        const updatedRules = [...rules];
        updatedRules[index][key] = value;
        setRules(updatedRules);
    };

    const removeRule = (index: number) => {
        const updatedRules = rules.filter((_, i) => i !== index);
        setRules(updatedRules);
    };

    const handleSubmit = () => {
        const payload = {
            name: intentName,
            description,
            workflows: linkedWorkflows,
            rules
        };
        console.log('Saving intent model:', payload);
        onClose();
    };

    const handleTest = () => {
        setTestOutput(`Detected intent: 'mock_intent'\nExtracted entities: { name: 'John', email: 'john@email.com' }`);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
            <div className="bg-neutral-900 text-white w-[90vw] max-w-5xl p-6 rounded-xl shadow-xl overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Create New Intent Model</h2>
                    <button onClick={onClose} className="hover:text-red-400">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Intent Name</label>
                        <input
                            type="text"
                            className="w-full p-2 rounded bg-neutral-800 text-white text-sm"
                            value={intentName}
                            onChange={(e) => setIntentName(e.target.value)}
                            placeholder="e.g. Invoice Intents"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Description</label>
                        <textarea
                            className="w-full p-2 rounded bg-neutral-800 text-white text-sm resize-none"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe the purpose of this intent group..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Linked Workflows</label>
                        <Select
                            isMulti
                            options={mockWorkflowOptions}
                            value={mockWorkflowOptions.filter(option => linkedWorkflows.includes(option.value))}
                            onChange={(selected) =>
                                setLinkedWorkflows(selected.map(option => option.value))
                            }
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    backgroundColor: '#1c1c1c',
                                    borderColor: '#333',
                                    color: 'white',
                                    padding: '2px',
                                }),
                                menu: (base) => ({
                                    ...base,
                                    backgroundColor: '#1c1c1c',
                                    color: 'white',
                                    zIndex: 9999,
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isFocused ? '#2a2a2a' : '#1c1c1c',
                                    color: 'white',
                                    cursor: 'pointer',
                                }),
                                multiValue: (base) => ({
                                    ...base,
                                    backgroundColor: '#333',
                                }),
                                multiValueLabel: (base) => ({
                                    ...base,
                                    color: 'white',
                                }),
                                multiValueRemove: (base) => ({
                                    ...base,
                                    color: '#ccc',
                                    ':hover': {
                                        backgroundColor: '#555',
                                        color: 'white',
                                    },
                                }),
                            }}
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Roles</label>
                        <Select
                            isMulti
                            options={[
                                { value: 'admin', label: 'Admin' },
                                { value: 'manager', label: 'Manager' },
                                { value: 'auditor', label: 'Auditor' },
                                { value: 'finance', label: 'Finance Team' },
                                { value: 'developer', label: 'Developer' },
                            ]}
                            value={selectedRoles.map((r) => ({ value: r, label: r }))}
                            onChange={(selectedOptions) =>
                                setSelectedRoles(selectedOptions.map((opt) => opt.value))
                            }
                            placeholder="Select roles..."
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    backgroundColor: '#1c1c1c',
                                    borderColor: '#333',
                                    color: 'white',
                                    padding: '2px',
                                }),
                                menu: (base) => ({
                                    ...base,
                                    backgroundColor: '#1c1c1c',
                                    color: 'white',
                                    zIndex: 9999,
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isFocused ? '#2a2a2a' : '#1c1c1c',
                                    color: 'white',
                                    cursor: 'pointer',
                                }),
                                multiValue: (base) => ({
                                    ...base,
                                    backgroundColor: '#333',
                                }),
                                multiValueLabel: (base) => ({
                                    ...base,
                                    color: 'white',
                                }),
                                multiValueRemove: (base) => ({
                                    ...base,
                                    color: '#ccc',
                                    ':hover': {
                                        backgroundColor: '#555',
                                        color: 'white',
                                    },
                                }),
                            }}
                        />

                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Users</label>
                        <Select
                            isMulti
                            options={[
                                { value: 'John Doe', label: 'John Doe' },
                                { value: 'Maryam Shah', label: 'Maryam Shah' },
                                { value: 'Ali Raza', label: 'Ali Raza' },
                                { value: 'Sana Khan', label: 'Sana Khan' },
                                { value: 'Daniel Lee', label: 'Daniel Lee' },
                            ]}
                            value={selectedUsers.map((id) => {
                                const match = userOptions.find((u) => u.value === id);
                                return match || { value: id, label: id };
                            })}
                            onChange={(selectedOptions) =>
                                setSelectedUsers(selectedOptions.map((opt) => opt.value))
                            }
                            placeholder="Assign users..."
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    backgroundColor: '#1c1c1c',
                                    borderColor: '#333',
                                    color: 'white',
                                    padding: '2px',
                                }),
                                menu: (base) => ({
                                    ...base,
                                    backgroundColor: '#1c1c1c',
                                    color: 'white',
                                    zIndex: 9999,
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isFocused ? '#2a2a2a' : '#1c1c1c',
                                    color: 'white',
                                    cursor: 'pointer',
                                }),
                                multiValue: (base) => ({
                                    ...base,
                                    backgroundColor: '#333',
                                }),
                                multiValueLabel: (base) => ({
                                    ...base,
                                    color: 'white',
                                }),
                                multiValueRemove: (base) => ({
                                    ...base,
                                    color: '#ccc',
                                    ':hover': {
                                        backgroundColor: '#555',
                                        color: 'white',
                                    },
                                }),
                            }}
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-400 mb-2">Intent Rules</label>
                        {rules.map((rule, index) => (
                            <div key={index} className="bg-neutral-800 p-3 rounded mb-3">
                                <div className="mb-2">
                                    <label className="text-xs block mb-1">Trigger Phrase</label>
                                    <input
                                        type="text"
                                        value={rule.phrase}
                                        onChange={(e) => updateRule(index, 'phrase', e.target.value)}
                                        className="w-full p-2 rounded bg-black text-white text-xs"
                                        placeholder="e.g. create an invoice"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="text-xs block mb-1">Required Entities (comma separated)</label>
                                    <input
                                        type="text"
                                        value={rule.requiredEntities.join(', ')}
                                        onChange={(e) => updateRule(index, 'requiredEntities', e.target.value.split(',').map(v => v.trim()))}
                                        className="w-full p-2 rounded bg-black text-white text-xs"
                                        placeholder="client_name, amount, due_date"
                                    />
                                </div>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <label className="text-xs block mb-1">Priority</label>
                                        <input
                                            type="number"
                                            value={rule.priority}
                                            onChange={(e) => updateRule(index, 'priority', parseInt(e.target.value))}
                                            className="w-24 p-2 rounded bg-black text-white text-xs"
                                        />
                                    </div>
                                    <button onClick={() => removeRule(index)} className="text-red-400 hover:text-red-300 mt-4">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button onClick={addRule} className="bg-neutral-700 hover:bg-neutral-600 text-white text-xs px-3 py-1 rounded flex items-center gap-1">
                            <Plus className="w-4 h-4" /> Add Rule
                        </button>
                    </div>

                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Test Input</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                className="flex-1 p-2 rounded bg-neutral-800 text-white text-sm"
                                value={sampleInput}
                                onChange={(e) => setSampleInput(e.target.value)}
                                placeholder="e.g. Generate an invoice for John Doe"
                            />
                            <button
                                onClick={handleTest}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
                            >
                                <MessageSquare className="w-4 h-4" /> Test
                            </button>
                        </div>
                        {testOutput && (
                            <pre className="mt-2 bg-black p-3 rounded text-xs text-green-400 whitespace-pre-wrap">
                                {testOutput}
                            </pre>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-neutral-800 pt-4">
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">Cancel</button>
                    <button onClick={handleSubmit} className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded text-sm">
                        Save Intent Model
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateIntentModelModal;
